import dayjs from "dayjs";
//import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { General } from "./constants";
import { Copy } from "./copy";
//dayjs.extend(customParseFormat);
dayjs.extend(utc);

export const getLocalDateTime = (date) => {
    if (!date) return "";
    const dateObj = new Date(date);
    // Check if it's a valid date and not the default .NET DateTime (01/01/0001)
    if (
      !(dateObj instanceof Date) ||
      isNaN(dateObj) ||
      dateObj.getFullYear() <= 1900
    ) {
      return "";
    }
    return dayjs(date).format(General.DateTimeSettings.Format.DateTimeFormat);
}

export const isValidDateTime = (date) => {
  if (!date) return false;
  const dateObj = new Date(date);
  // Check if it's a valid date and not the default .NET DateTime (01/01/0001)
  return (
    dateObj instanceof Date && !isNaN(dateObj) && dateObj.getFullYear() > 1900
  );
};

export const convertToLocalDateTime = (utcDateString) => {
  if (!utcDateString) {
    return "";
  }

    var localDate = new Date(utcDateString);

    // Define options for named months and local formatting
    var options = {
      year: "numeric",
      month: "long", // Full month name (e.g., "January", "February")
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false, // Optional: set to true for 12-hour format
      timeZoneName: "short", // Optional: adds time zone abbreviation
    };

    // Use the browser's default locale for formatting
    return localDate.toLocaleDateString(undefined, options);
};

/** Minutes the browser's zone is ahead of UTC right now, e.g. 600 for UTC+10:00, -330 for UTC-05:30. */
export const getLocalUtcOffsetMinutes = () => -new Date().getTimezoneOffset();

/** An offset in minutes as the conventional label, e.g. 600 -> "UTC+10:00", -330 -> "UTC-05:30", 0 -> "UTC+00:00". */
export const formatUtcOffset = (offsetMinutes) => {
  const sign = offsetMinutes < 0 ? "-" : "+";
  const hours = Math.floor(Math.abs(offsetMinutes) / 60).toString().padStart(2, "0");
  const minutes = (Math.abs(offsetMinutes) % 60).toString().padStart(2, "0");
  return `UTC${sign}${hours}:${minutes}`;
};

/**
 * The weekday and hour a UTC week slot falls on in a zone given as minutes ahead of UTC.
 * weekday is a 0..6 cycle in whatever convention the caller uses (the result keeps it), hour is
 * 0..23; the shift carries across midnight and across the week boundary. Slots are whole hours,
 * so a half-hour zone moves by its nearest whole hour, and one offset is applied because a week
 * slot carries no date to resolve a daylight saving change against.
 */
export const toLocalWeekHour = ({ weekday, hour }, offsetMinutes) => {
  const hoursInWeek = 168;
  const shift = Math.round(offsetMinutes / 60);
  const weekHour = (((weekday * 24 + hour + shift) % hoursInWeek) + hoursInWeek) % hoursInWeek;
  return { weekday: Math.floor(weekHour / 24), hour: weekHour % 24 };
};

/**
 * Chart x-axis labels in the viewer's local time, from the UTC instants at which each
 * chart bucket begins (the proxy's chartBucketStartTimesUtc, one per data point). The
 * server cannot know the viewer's timezone, so it sends the raw instants and the
 * browser formats them here: a month-and-day when buckets are a day or wider, a clock
 * time otherwise. The bucket width is read from the spacing of the instants
 * themselves, so no range-to-width table is mirrored on the client. fallbackLabels
 * (the payload's pre-formatted UTC text) is returned when the payload predates
 * chartBucketStartTimesUtc.
 */
export const toLocalChartBucketLabels = (chartBucketStartTimesUtc, fallbackLabels) => {
  if (!Array.isArray(chartBucketStartTimesUtc) || chartBucketStartTimesUtc.length === 0) {
    return fallbackLabels;
  }

  const millisecondsInDay = 86400000;
  const dates = chartBucketStartTimesUtc.map((instant) => new Date(instant));
  const bucketsAreDaysOrWider =
    dates.length > 1 && dates[1].getTime() - dates[0].getTime() >= millisecondsInDay;

  return dates.map((date) => {
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return bucketsAreDaysOrWider
      ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  });
};

export const convertToUTCDateTime = (local) => {
    if (!local) {
        return;
    }
    let converted = new Date(local);

    return converted.toUTCString();
}

/**
 * Minutes from one instant to a later one.
 * @param {Date|null} from
 * @param {Date|null} to
 * @returns {number|null} null when either is missing or to is before from
 */
export const minutesBetween = (from, to) => (from && to && to >= from ? (to - from) / 60000 : null);

/**
 * Returns a human-readable relative time string (e.g., "Just now", "5m ago", "2h ago", "3d ago")
 * Falls back to formatted date for older dates.
 * @param {string|Date} date - The date to format
 * @param {{ showSeconds?: boolean }} [options] - showSeconds reports the first minute as "40s ago" instead of "Just now", for readings refreshed more often than once a minute
 * @returns {string} Relative time string
 */
export const getRelativeTimeAgo = (date, { showSeconds = false } = {}) => {
    if (!date) return '';
    
    const dateObj = new Date(date);
    if (!(dateObj instanceof Date) || isNaN(dateObj) || dateObj.getFullYear() <= 1900) {
        return '';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (showSeconds && diffSecs >= 1 && diffMins < 1) return `${diffSecs}s ago`;
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Time remaining until a future instant, in words: "4 minutes" / "32 seconds".
 * The future twin of getRelativeTimeAgo. Returns null when the instant has
 * passed (or the input is invalid), so callers decide the "now" wording.
 * @param {string|Date} date - The future instant
 * @param {number} [nowMs] - Reference clock, defaults to Date.now(); injectable for ticking components and tests
 * @returns {string|null}
 */
export const getRelativeTimeUntil = (date, nowMs = Date.now()) => {
    if (!date) return null;

    const dateObj = new Date(date);
    if (isNaN(dateObj)) return null;

    const totalSeconds = Math.ceil((dateObj.getTime() - nowMs) / 1000);
    if (totalSeconds <= 0) return null;

    if (totalSeconds >= 60) {
        const minutes = Math.ceil(totalSeconds / 60);
        return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }

    return `${totalSeconds} second${totalSeconds === 1 ? '' : 's'}`;
}

// A cron field that names one fixed value, such as the 30 in "30 * * * *".
const isFixedCronField = (field) => String(Number.parseInt(field, 10)) === field;

/**
 * Format a cron expression into a human-readable description.
 * Handles common presets (hourly, daily, weekly, monthly) and falls back
 * to returning the raw expression for unrecognised patterns.
 * @param {string} cronExpression - A standard 5-part cron expression
 * @returns {string} Human-readable description or the original expression
 */
export const describeCron = (cronExpression) => {
  if (!cronExpression) return '';

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length < 5) return cronExpression;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Simple common cases
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && minute === '*' && hour === '*') {
    return 'Every minute';
  }
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && isFixedCronField(minute) && hour === '*') {
    return 'Hourly';
  }
  // Daily needs a fixed minute and hour; a starred hour already matched above.
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && minute !== '*' && hour !== '*' && !minute.includes('/') && !hour.includes('/')) {
    return 'Daily';
  }
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '5') {
    return 'Weekly on Friday';
  }
  if (dayOfMonth === '1' && month === '*' && dayOfWeek === '*') {
    return 'Monthly on the 1st';
  }

  return cronExpression;
};

/**
 * Estimate how many times a cron expression fires over a given number of months.
 * Uses simple heuristics based on the cron pattern — accurate for common presets,
 * approximate for complex expressions.
 * @param {string} cronExpression - A standard 5-part cron expression
 * @param {number} months - Number of months to estimate over
 * @returns {number|null} Estimated execution count, or null if unable to estimate
 */
export const estimateCronExecutions = (cronExpression, months) => {
  if (!cronExpression || !months || months <= 0) return null;

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length < 5) return null;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  const totalDays = Math.round(months * 30.44); // average days per month

  // Every N minutes  e.g. */5 * * * *
  if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const interval = parseInt(minute.substring(2), 10);
    if (interval > 0) return Math.floor((totalDays * 24 * 60) / interval);
  }

  // Every N hours  e.g. 0 */2 * * *
  if (hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const interval = parseInt(hour.substring(2), 10);
    if (interval > 0) return Math.floor((totalDays * 24) / interval);
  }

  // Hourly  e.g. 0 * * * *
  if (isFixedCronField(minute) && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return totalDays * 24;
  }

  // Daily  e.g. 0 2 * * *
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && !minute.includes('/') && !hour.includes('/')) {
    return totalDays;
  }

  // Weekly  e.g. 0 2 * * 5
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*' && !dayOfWeek.includes(',') && !dayOfWeek.includes('-')) {
    return Math.floor(totalDays / 7);
  }

  // Monthly  e.g. 0 2 1 * *
  if (dayOfMonth !== '*' && !dayOfMonth.includes(',') && month === '*' && dayOfWeek === '*') {
    return months;
  }

  return null;
};

/**
 * Format a date string into a short localized date (e.g. "Mar 7, 2026").
 * @param {string|Date} dateStr - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

/**
 * Format a date string into a short localized time (e.g. "9:05 PM").
 * @param {string|Date} dateStr - The date to format
 * @returns {string} Formatted time string
 */
export const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

/**
 * An instant as a list row's stamp, in local time: always its day and time ("4 Sep 21:43"),
 * today included, since a row in a list is read beside rows of other days. The day and the time
 * are written as formatInstant writes them (further down), so one instant reads the same on
 * every screen.
 * @param {number | string | Date} instant Epoch milliseconds, a UTC instant as text, or a date.
 * @returns {string | null} Null when there is no instant.
 */
export const formatShortDateTime = (instant) => {
    if (!instant) {
        return null;
    }

    const date = new Date(instant);

    return `${dayAndMonth(date)} ${clock(date)}`;
};

// A schedule preset leaves its time of day open. These stand for the minute and the hour in a
// preset, and buildCronExpressionFromPreset fills them in when the schedule is saved.
const MINUTE_PLACEHOLDER = '{minute}';
const HOUR_PLACEHOLDER = '{hour}';

/**
 * The preset a new schedule starts on.
 */
export const DAILY_CRON_PRESET = `${MINUTE_PLACEHOLDER} ${HOUR_PLACEHOLDER} * * *`;

/**
 * Common cron schedule presets for UI dropdowns.
 * Each preset has a human-readable label and a 5-part cron expression whose time of day is left
 * open, so that schedules made at different moments do not all run at the same minute.
 * The 'Custom' entry uses an empty value to signal free-form input.
 */
export const CRON_PRESETS = [
  { label: 'Every minute', value: '* * * * *' },
  { label: 'Hourly', value: `${MINUTE_PLACEHOLDER} * * * *` },
  { label: 'Daily', value: DAILY_CRON_PRESET },
  { label: 'Weekly (Friday)', value: `${MINUTE_PLACEHOLDER} ${HOUR_PLACEHOLDER} * * 5` },
  { label: 'Monthly (1st)', value: `${MINUTE_PLACEHOLDER} ${HOUR_PLACEHOLDER} 1 * *` },
  { label: 'Custom (cron expression)', value: '' },
];

/**
 * Builds the cron expression of a schedule preset, giving it the hour and minute of the moment
 * the schedule is saved. The API reads cron in UTC, so that moment's UTC hour and minute are used.
 * @param {string} preset - A value from CRON_PRESETS
 * @param {Date} [savedAt] - The moment the schedule is saved; now when omitted
 * @returns {string} A standard 5-part cron expression
 */
export const buildCronExpressionFromPreset = (preset, savedAt = new Date()) =>
  preset
    .replace(MINUTE_PLACEHOLDER, String(savedAt.getUTCMinutes()))
    .replace(HOUR_PLACEHOLDER, String(savedAt.getUTCHours()));

/**
 * Finds the preset a cron expression was made from, whatever time of day it carries.
 * @param {string} cronExpression - A standard 5-part cron expression
 * @returns {string} The matching value from CRON_PRESETS, or '' when it matches none
 */
export const findCronPreset = (cronExpression) => {
  const parts = (cronExpression || '').trim().split(/\s+/);

  const match = CRON_PRESETS.find((preset) => {
    const presetParts = preset.value.split(' ');

    return (
      presetParts.length === parts.length &&
      presetParts.every((presetPart, index) =>
        presetPart === MINUTE_PLACEHOLDER || presetPart === HOUR_PLACEHOLDER
          ? isFixedCronField(parts[index])
          : presetPart === parts[index]
      )
    );
  });

  return match ? match.value : '';
};

/**
 * The cron expression a schedule form saves.
 * A custom expression is saved as typed. A preset takes its time of day from the moment of
 * saving, unless the schedule already exists and was made from that same preset: it then keeps
 * the expression it has, so that editing a schedule does not move its time of day.
 * @param {object} schedule
 * @param {string} schedule.preset - The chosen value from CRON_PRESETS ('' for a custom expression)
 * @param {string} schedule.customCronExpression - The typed expression, used when no preset is chosen
 * @param {string} [schedule.currentCronExpression] - The expression the schedule has now, when it is being edited
 * @param {Date} [schedule.savedAt] - The moment of saving; now when omitted
 * @returns {string} A standard 5-part cron expression
 */
export const getCronExpressionToSave = ({
  preset,
  customCronExpression,
  currentCronExpression = '',
  savedAt = new Date(),
}) => {
  if (!preset) return customCronExpression;
  if (currentCronExpression && findCronPreset(currentCronExpression) === preset) return currentCronExpression;

  return buildCronExpressionFromPreset(preset, savedAt);
};

/**
 * Format a duration in minutes into a short human-readable label.
 * Examples: 0.5 → "30 secs", 3.2 → "3.2 mins", 90 → "1h 30m", 1500 → "1d 1h"
 * @param {number|null|undefined} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 */
export const formatMinutes = (minutes) => {
    if (minutes == null || minutes === 0) return "—";
    if (minutes < 1) return `${Math.round(minutes * 60)} secs`;
    if (minutes < 60) return `${Math.round(minutes * 10) / 10} mins`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours >= 24) {
        const days = Math.floor(hours / 24);
        const remHours = hours % 24;
        return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
    }
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

/** Split a duration in minutes into { value, unit } for KPIValueHeading */
export const splitMinutes = (minutes) => {
    if (minutes == null || minutes === 0) return { value: "—", unit: "" };
    if (minutes < 1) return { value: Math.round(minutes * 60), unit: "secs" };
    if (minutes < 60) return { value: Math.round(minutes * 10) / 10, unit: "mins" };
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours >= 24) {
        const days = Math.floor(hours / 24);
        const remHours = hours % 24;
        return remHours > 0 ? { value: `${days}d ${remHours}`, unit: "h" } : { value: days, unit: "d" };
    }
    return mins > 0 ? { value: `${hours}h ${mins}`, unit: "m" } : { value: hours, unit: "h" };
};

/**
 * Format a duration in days into a human-readable label.
 * Examples: 0.3 → "7 hours", 2.5 → "2.5 days"
 * @param {number|null|undefined} days - Duration in days
 * @returns {string} Formatted duration string
 */
export const formatDays = (days) => {
    if (days == null || days === 0) return "—";
    if (days < 1) return `${Math.round(days * 24)} hours`;
    return `${Math.round(days * 10) / 10} days`;
};

/** Abbreviated month names for chart axis / date labels. */
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * The calendar day a Date falls on in the viewer's local time, as "YYYY-MM-DD". Use this
 * rather than toISOString().slice(0, 10), which gives the UTC day and lands on the wrong
 * date for part of every day in any zone away from UTC.
 * @param {Date} date
 * @returns {string} e.g. "2026-03-08"
 */
export const toLocalDayKey = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * A "YYYY-MM-DD" day key as a short chart label, "Mar 8". Reads the parts directly:
 * new Date("2026-03-08") is UTC midnight, and local getters would shift the label a day
 * for viewers west of UTC.
 * @param {string} dayKey
 * @returns {string}
 */
export const formatDayKeyLabel = (dayKey) => {
    const [, month, day] = dayKey.split("-").map(Number);
    return `${MONTHS[month - 1]} ${day}`;
};

/**
 * A "YYYY-MM-DD" day key as its year, its month's short name and its day, "2026 Oct 7": the days of
 * the Summary Usage Stats chart of an API's assets.
 * @param {string} dayKey
 * @returns {string}
 */
export const formatDayKeyWithYear = (dayKey) => {
    const [year, month, day] = dayKey.split("-").map(Number);
    return `${year} ${MONTHS[month - 1]} ${day}`;
};

/**
 * A "YYYY-MM-DD" day key as its day, month and year in figures, "07/10/2026": the days of the
 * Summary Usage Stats chart of a service's page.
 * @param {string} dayKey
 * @returns {string}
 */
export const formatDayKeyInFigures = (dayKey) => dayKey.split("-").reverse().join("/");

/**
 * The first and last day of a stretch of time between two instants, in the viewer's local time,
 * as chart labels name days: "Jul 22 to Oct 20".
 * @param {string | number | Date} from The stretch's first instant (an ISO instant ends in Z).
 * @param {string | number | Date} to Its last instant.
 * @returns {string}
 */
export const formatLocalDayRange = (from, to) =>
    Copy.Common.DayRange(formatDayKeyLabel(toLocalDayKey(new Date(from))), formatDayKeyLabel(toLocalDayKey(new Date(to))));

/**
 * A "YYYY-MM-DD" day key as a Date at local midnight. new Date("2026-03-08") would parse
 * it as UTC midnight, which is the previous evening for viewers west of UTC.
 * @param {string} dayKey
 * @returns {Date}
 */
export const parseLocalDayKey = (dayKey) => {
    const [year, month, day] = dayKey.split("-").map(Number);
    return new Date(year, month - 1, day);
};

/**
 * The day a number of days before or after a "YYYY-MM-DD" day key, on the viewer's calendar.
 * @param {string} dayKey
 * @param {number} days Negative for a day before.
 * @returns {string} e.g. shiftLocalDayKey("2026-12-31", 1) is "2027-01-01"
 */
export const shiftLocalDayKey = (dayKey, days) => {
    const day = parseLocalDayKey(dayKey);
    day.setDate(day.getDate() + days);
    return toLocalDayKey(day);
};

/**
 * The last moment of a "YYYY-MM-DD" day on the viewer's calendar, as the UTC instant a service reads
 * (CommonLayer.Constants.IsoUtcDateTimeFormat): 29 October 2027 ends at "2027-10-29T12:59:59.999Z" in
 * Sydney and at "2027-10-30T06:59:59.999Z" in Los Angeles.
 * @param {string} dayKey
 * @returns {string}
 */
export const toUtcEndOfLocalDay = (dayKey) => {
    const day = parseLocalDayKey(dayKey);
    day.setHours(23, 59, 59, 999);
    return day.toISOString();
};

/**
 * Every calendar day of a stretch of time in the viewer's local time, first to last, as day keys:
 * from the day its first instant falls on to the day its last one falls on.
 * @param {string | number | Date} from The stretch's first instant (an ISO instant ends in Z).
 * @param {string | number | Date} to Its last instant.
 * @returns {string[]} e.g. ["2026-10-13", "2026-10-14"]; none when either end is not a real instant.
 */
export const listLocalDayKeys = (from, to) => {
    // An end that is not a real instant gives no days, so the walk below always ends.
    if (!isValidDateTime(from) || !isValidDateTime(to)) {
        return [];
    }

    const lastDayKey = toLocalDayKey(new Date(to));
    const dayKeys = [];

    for (const day = parseLocalDayKey(toLocalDayKey(new Date(from))); toLocalDayKey(day) <= lastDayKey; day.setDate(day.getDate() + 1)) {
        dayKeys.push(toLocalDayKey(day));
    }

    return dayKeys;
};

/**
 * An instant as a date and time field takes it (an input of type "datetime-local"): local
 * time, to the second.
 * @param {number} instant Epoch milliseconds.
 * @returns {string} e.g. "2026-03-08T14:31:37"
 */
export const toLocalInputValue = (instant) => {
    const date = new Date(instant);
    const clock = [date.getHours(), date.getMinutes(), date.getSeconds()].map((part) => String(part).padStart(2, "0")).join(":");

    return `${toLocalDayKey(date)}T${clock}`;
};

/**
 * The instant a date and time field holds, or null while it holds no whole date and time.
 * @param {string} value
 * @returns {number | null} Epoch milliseconds.
 */
export const fromLocalInputValue = (value) => {
    const instant = value ? new Date(value).getTime() : NaN;

    return Number.isNaN(instant) ? null : instant;
};

/**
 * A range of time moved by its own length, earlier or later, and never past the present.
 * @param {number} from Epoch milliseconds.
 * @param {number} to Epoch milliseconds.
 * @param {number} direction -1 for earlier, 1 for later.
 * @param {number} now Epoch milliseconds.
 * @returns {{ from: number, to: number }}
 */
export const shiftedTimeRange = (from, to, direction, now) => {
    const length = to - from;
    const end = Math.min(to + direction * length, now);

    return { from: end - length, to: end };
};

/**
 * A range of time once a new start is typed. A start at or after the end takes the end with it,
 * so the range keeps its length, though it never runs past the present. Null when that leaves
 * no range: a start at or after the present.
 * @param {number} from Epoch milliseconds.
 * @param {number} to Epoch milliseconds.
 * @param {number} start The start typed, as epoch milliseconds.
 * @param {number} now Epoch milliseconds.
 * @returns {{ from: number, to: number } | null}
 */
export const timeRangeStartingAt = (from, to, start, now) => {
    const end = start < to ? to : Math.min(start + (to - from), now);

    return start < end ? { from: start, to: end } : null;
};

/**
 * A range of time once a new end is typed. An end at or before the start takes the start with
 * it, so the range keeps its length.
 * @param {number} from Epoch milliseconds.
 * @param {number} to Epoch milliseconds.
 * @param {number} end The end typed, as epoch milliseconds.
 * @returns {{ from: number, to: number }}
 */
export const timeRangeEndingAt = (from, to, end) => ({ from: end > from ? from : end - (to - from), to: end });

const { Durations, TimeSpans } = Copy.Common;

const { MsPerSecond, MsPerMinute, MsPerHour, MsPerDay } = General.Time;

const padded = (number, width = 2) => String(number).padStart(width, "0");

const sameLocalDay = (first, second) =>
    first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();

const clock = (date) => `${padded(date.getHours())}:${padded(date.getMinutes())}`;
const dayAndMonth = (date) => `${date.getDate()} ${MONTHS[date.getMonth()]}`;

/**
 * A duration in milliseconds as a person reads it: "88 ms", "1.28 s", "30.4 s", "2.5 min",
 * "3.0 h". The number is rounded first and the unit chosen from what it rounds to, so a duration
 * just under a unit is written in that unit: 999.5 ms is "1.00 s", never "1000 ms".
 * @param {number} durationMs
 * @returns {string} Empty when there is no duration to write.
 */
export const formatDuration = (durationMs) => {
    if (!Number.isFinite(durationMs)) {
        return "";
    }

    const milliseconds = Math.round(durationMs);

    if (milliseconds < MsPerSecond) {
        return Durations.Milliseconds(milliseconds);
    }

    const seconds = durationMs / MsPerSecond;

    if (Number(seconds.toFixed(2)) < 10) {
        return Durations.Seconds(seconds.toFixed(2));
    }

    if (Number(seconds.toFixed(1)) < 60) {
        return Durations.Seconds(seconds.toFixed(1));
    }

    const minutes = durationMs / MsPerMinute;

    if (Number(minutes.toFixed(1)) < 60) {
        return Durations.Minutes(minutes.toFixed(1));
    }

    return Durations.Hours((durationMs / MsPerHour).toFixed(1));
};

/**
 * A stretch of time in words, in whole units: "45 seconds", "23 minutes", "6 hours", "2 days".
 * @param {number} spanMs
 * @returns {string}
 */
export const formatSpan = (spanMs) => {
    if (spanMs >= 2 * MsPerDay) {
        return TimeSpans.Day(Math.round(spanMs / MsPerDay));
    }

    if (spanMs >= 2 * MsPerHour) {
        return TimeSpans.Hour(Math.round(spanMs / MsPerHour));
    }

    if (spanMs >= 2 * MsPerMinute) {
        return TimeSpans.Minute(Math.round(spanMs / MsPerMinute));
    }

    return TimeSpans.Second(Math.max(1, Math.round(spanMs / MsPerSecond)));
};

/**
 * An instant as a short label, in local time: the time for one today ("14:31"), the day and
 * time otherwise ("2 Oct 09:05").
 * @param {number | string} instant Epoch milliseconds, or a UTC instant as text.
 * @param {number} now Epoch milliseconds.
 * @returns {string}
 */
export const formatInstant = (instant, now) => {
    const date = new Date(instant);

    return sameLocalDay(date, new Date(now)) ? clock(date) : `${dayAndMonth(date)} ${clock(date)}`;
};

/**
 * The two ends of a range of time as short labels, in local time: the times alone when both
 * ends are today ("18:14" and "19:14"), the day and time on both otherwise ("28 Sep 18:14" and
 * "5 Oct 19:14"). Written one by one with formatInstant, an end that is today loses its day,
 * and a week that ends today reads as an hour on its first day.
 * @param {number} from Epoch milliseconds.
 * @param {number} to Epoch milliseconds.
 * @param {number} now Epoch milliseconds.
 * @returns {{ from: string, to: string }}
 */
export const formatTimeRange = (from, to, now) => {
    const start = new Date(from);
    const end = new Date(to);
    const today = new Date(now);
    const bothToday = sameLocalDay(start, today) && sameLocalDay(end, today);
    const label = (date) => (bothToday ? clock(date) : `${dayAndMonth(date)} ${clock(date)}`);

    return { from: label(start), to: label(end) };
};

/**
 * An instant to the millisecond, in local time: the time alone for one today ("14:31:37.480"),
 * with its day in front otherwise ("2 Oct 09:05:03.007").
 * @param {number | string} instant Epoch milliseconds, or a UTC instant as text.
 * @param {number} now Epoch milliseconds.
 * @returns {string}
 */
export const formatPreciseInstant = (instant, now) => {
    const date = new Date(instant);
    const time = `${clock(date)}:${padded(date.getSeconds())}.${padded(date.getMilliseconds(), 3)}`;

    return sameLocalDay(date, new Date(now)) ? time : `${dayAndMonth(date)} ${time}`;
};

/**
 * A tick under a chart of something over time, by how much time the chart covers: the time
 * across a day or less, the day and time across a few days, the day alone across longer.
 * @param {number} instant Epoch milliseconds.
 * @param {number} spanMs The time the chart covers.
 * @returns {string}
 */
export const formatAxisTick = (instant, spanMs) => {
    const date = new Date(instant);

    if (spanMs <= MsPerDay) {
        return spanMs <= 10 * MsPerMinute ? `${clock(date)}:${padded(date.getSeconds())}` : clock(date);
    }

    return spanMs <= 4 * MsPerDay ? `${dayAndMonth(date)} ${clock(date)}` : dayAndMonth(date);
};

/**
 * Parse an ISO week string "2026-W10" into a Date for the Monday of that week.
 * @param {string} weekStr - ISO week string in "YYYY-Www" format
 * @returns {Date} The Monday of the given ISO week
 */
export const parseISOWeek = (weekStr) => {
    const [yearStr, wStr] = weekStr.split("-W");
    const year = parseInt(yearStr, 10);
    const week = parseInt(wStr, 10);
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    return monday;
};

/**
 * Format a Date back to ISO week string "YYYY-Www".
 * @param {Date} date - The date to convert
 * @returns {string} ISO week string (e.g. "2026-W10")
 */
export const toISOWeekStr = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((d - yearStart) / 86_400_000 + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
};
